import React, { useState, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Card, Button, Form as BsForm } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';

// Mensagens literais do .pas:638/643/648/654 (FE-8)
const MSG_GE = 'Necessário escolher algum GE.';
const MSG_STATUS = 'Necessário selecionar pelo menos um Status.';
const MSG_ESTAGIO = 'Necessário selecionar pelo menos um Estágio.';
const MSG_SEGMENTO = 'Necessário selecionar pelo menos um Segmento.';

// Defaults do .dfm (FE-1 — Checado/desmarcado exato)
const VALORES_INICIAIS = {
  gesMulti: [],
  grupoGe: null,
  empreendimentos: [],
  cidades: [],
  // Status — D/P/O/B/A/R marcados; V(Carteira) desmarcado
  statusDisponivel: true,
  statusPreVenda: true,
  statusCadRapido: true,
  statusBloqueado: true,
  statusAlugado: true,
  statusReservado: true,
  carteira: false,
  // Estágio — todos 6 marcados (.dfm:1640-1699)
  estagioEstudos: true,
  estagioAprovacao: true,
  estagioObras: true,
  estagioRegistro: true,
  estagioLancamento: true,
  estagioFinalizado: true,
  // Segmento — todos 4 marcados (.dfm:1797-1836)
  segmentoEstoque: true,
  segmentoAreaBruta: true,
  segmentoTerreno: true,
  segmentoProperty: true,
  // Opções (.dfm:1838-1968)
  somenteSaldo: true,       // ckSomenteSaldo MARCADO (FD-1 — gap material)
  dataLancamento: '',
  levantaTempoEstoque: true, // chDesde MARCADO (GAP-005)
  pontuacao: false,
  ordemPontuacao: 0,
  agrupamento: 0,           // 0=GE, 1=Cidade (rgOrdem default GE)
  imprimirCustos: false,
  totaliza: false,
};

const validationSchema = Yup.object().shape({
  gesMulti: Yup.array(),
  grupoGe: Yup.object().nullable(),
})
  .test('ge-obrigatorio', MSG_GE, (v) => (v.gesMulti && v.gesMulti.length > 0) || v.grupoGe != null)
  .test('status-obrigatorio', MSG_STATUS, (v) =>
    v.statusDisponivel || v.statusPreVenda || v.statusCadRapido ||
    v.statusBloqueado || v.statusAlugado || v.statusReservado || v.carteira
  )
  .test('estagio-obrigatorio', MSG_ESTAGIO, (v) =>
    v.estagioEstudos || v.estagioAprovacao || v.estagioObras ||
    v.estagioRegistro || v.estagioLancamento || v.estagioFinalizado
  )
  .test('segmento-obrigatorio', MSG_SEGMENTO, (v) =>
    v.segmentoEstoque || v.segmentoAreaBruta || v.segmentoTerreno || v.segmentoProperty
  );

const buildPayload = (values) => ({
  grupos_empresa_csv: values.gesMulti.length > 0
    ? values.gesMulti.map((g) => g.value).join(',')
    : null,
  codigo_grupo_ge: values.gesMulti.length === 0 && values.grupoGe
    ? values.grupoGe.value
    : null,
  empreendimentos_csv: values.empreendimentos.length > 0
    ? values.empreendimentos.map((e) => e.value).join(',')
    : null,
  cidades_csv: values.cidades.length > 0
    ? values.cidades.map((c) => c.value).join(',')
    : null,
  status_disponivel: values.statusDisponivel,
  status_pre_venda: values.statusPreVenda,
  status_cad_rapido: values.statusCadRapido,
  status_bloqueado: values.statusBloqueado,
  status_alugado: values.statusAlugado,
  status_reservado: values.statusReservado,
  carteira: values.carteira,
  estagio_estudos: values.estagioEstudos,
  estagio_aprovacao: values.estagioAprovacao,
  estagio_obras: values.estagioObras,
  estagio_registro: values.estagioRegistro,
  estagio_lancamento: values.estagioLancamento,
  estagio_finalizado: values.estagioFinalizado,
  segmento_estoque: values.segmentoEstoque,
  segmento_area_bruta: values.segmentoAreaBruta,
  segmento_terreno: values.segmentoTerreno,
  segmento_property: values.segmentoProperty,
  somente_saldo: values.somenteSaldo,
  data_lancamento: values.dataLancamento ? values.dataLancamento : null,
  levanta_tempo_estoque: values.levantaTempoEstoque,
  pontuacao: values.pontuacao,
  ordem_pontuacao: values.ordemPontuacao,
  agrupamento: values.agrupamento,
  imprimir_custos: values.imprimirCustos,
  totaliza: values.totaliza,
});

const OPCOES_ORDEM_PONTUACAO = [
  { value: 0, label: 'Selecionar Ordem' },
  { value: 1, label: 'Do Menor para o Maior' },
  { value: 2, label: 'Do Maior para o Menor' },
];

const FiltrosImoveisEstoque = ({ onSubmit, onLimpar, isLoading, opcoesGes, opcoesGruposGe, valoresIniciais: valoresIniciaisExt }) => {
  const [opcoesEmpreendimentos, setOpcoesEmpreendimentos] = useState([]);
  const [opcoesCidades, setOpcoesCidades] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [loadingCid, setLoadingCid] = useState(false);
  // Anti-race: ignora resposta de chamada anterior ao mudar GE
  const reqGenerationRef = useRef(0);

  const gesSelectOpcoes = (opcoesGes || []).map((g) => ({
    value: g.codigo_grupo_empresa,
    label: g.apelido,
  }));

  const gruposGeOpcoes = (opcoesGruposGe || []).map((g) => ({
    value: g.codigo,
    label: `${g.codigo} - ${g.descricao}`,
  }));

  const carregarEmpCid = (gesCsv) => {
    if (!gesCsv) {
      setOpcoesEmpreendimentos([]);
      setOpcoesCidades([]);
      return;
    }
    reqGenerationRef.current += 1;
    const gen = reqGenerationRef.current;
    const req = { grupos_empresa_csv: gesCsv, ja_selecionados_csv: null };
    setLoadingEmp(true);
    setLoadingCid(true);
    Promise.all([
      api.listarEmpreendimentosImoveisEstoque(req),
      api.listarCidadesImoveisEstoque(req),
    ])
      .then(([emps, cids]) => {
        if (reqGenerationRef.current !== gen) return;
        setOpcoesEmpreendimentos(
          (Array.isArray(emps) ? emps : []).map((e) => ({
            value: e.codigo_familia,
            label: e.nome_da_familia,
          }))
        );
        setOpcoesCidades(
          (Array.isArray(cids) ? cids : []).map((c) => ({
            value: c.codigo_cidade,
            label: c.descricao,
          }))
        );
      })
      .catch((err) => {
        if (reqGenerationRef.current !== gen) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar empreendimentos/cidades.';
        toast.error(msg);
      })
      .finally(() => {
        if (reqGenerationRef.current !== gen) return;
        setLoadingEmp(false);
        setLoadingCid(false);
      });
  };

  const initialValues = valoresIniciaisExt
    ? Object.assign({}, VALORES_INICIAIS, valoresIniciaisExt)
    : VALORES_INICIAIS;

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values) => {
        onSubmit(buildPayload(values));
      }}
    >
      {({ values, setFieldValue, errors, isSubmitting, resetForm }) => {
        const gesCsvAtual = values.gesMulti.length > 0
          ? values.gesMulti.map((g) => g.value).join(',')
          : null;

        const handleGeChange = (selecionados) => {
          setFieldValue('gesMulti', selecionados || []);
          if (selecionados && selecionados.length > 0) {
            setFieldValue('grupoGe', null);
          }
          const csv = selecionados && selecionados.length > 0
            ? selecionados.map((g) => g.value).join(',')
            : null;
          carregarEmpCid(csv);
          setFieldValue('empreendimentos', []);
          setFieldValue('cidades', []);
        };

        const handleGrupoGeChange = (opcao) => {
          setFieldValue('grupoGe', opcao);
          if (opcao) {
            setFieldValue('gesMulti', []);
          }
          setFieldValue('empreendimentos', []);
          setFieldValue('cidades', []);
          setOpcoesEmpreendimentos([]);
          setOpcoesCidades([]);
        };

        const handleCarteiraChange = (e) => {
          const checked = e.target.checked;
          setFieldValue('carteira', checked);
          if (checked) setFieldValue('somenteSaldo', false);
        };

        const handleSomenteSaldoChange = (e) => {
          const checked = e.target.checked;
          setFieldValue('somenteSaldo', checked);
          if (checked) setFieldValue('carteira', false);
        };

        const handlePontuacaoChange = (e) => {
          const checked = e.target.checked;
          setFieldValue('pontuacao', checked);
          if (!checked) setFieldValue('ordemPontuacao', 0);
        };

        const handleLimpar = () => {
          resetForm({ values: VALORES_INICIAIS });
          setOpcoesEmpreendimentos([]);
          setOpcoesCidades([]);
          onLimpar();
        };

        const errosLista = Object.values(errors).filter(Boolean);

        return (
          <Form>
            <Card className="mb-3">
              <Card.Header><strong>Filtros</strong></Card.Header>
              <Card.Body>
                {errosLista.length > 0 && (
                  <div className="alert alert-danger py-2">
                    {errosLista.map((msg) => (
                      <div key={msg}>{msg}</div>
                    ))}
                  </div>
                )}

                {/* GE e Grupo-GE */}
                <Row className="mb-2">
                  <Col md={6}>
                    <BsForm.Label>GE</BsForm.Label>
                    <Select
                      isMulti
                      options={gesSelectOpcoes}
                      value={values.gesMulti}
                      onChange={handleGeChange}
                      placeholder="Selecione GE(s)..."
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={values.grupoGe != null}
                      noOptionsMessage={() => 'Nenhuma GE encontrada'}
                    />
                  </Col>
                  <Col md={6}>
                    <BsForm.Label>Grupo-GE</BsForm.Label>
                    <Select
                      options={gruposGeOpcoes}
                      value={values.grupoGe}
                      onChange={handleGrupoGeChange}
                      placeholder="Selecione um Grupo-GE..."
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isClearable
                      isDisabled={values.gesMulti.length > 0}
                      noOptionsMessage={() => 'Nenhum grupo encontrado'}
                    />
                  </Col>
                </Row>

                {/* Empreendimento e Cidade */}
                <Row className="mb-2">
                  <Col md={6}>
                    <BsForm.Label>Empreendimento</BsForm.Label>
                    <Select
                      isMulti
                      options={opcoesEmpreendimentos}
                      value={values.empreendimentos}
                      onChange={(s) => setFieldValue('empreendimentos', s || [])}
                      placeholder="Selecione empreendimento(s)..."
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingEmp}
                      noOptionsMessage={() =>
                        gesCsvAtual ? 'Nenhum empreendimento encontrado' : 'Selecione uma GE primeiro'
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <BsForm.Label>Cidade</BsForm.Label>
                    <Select
                      isMulti
                      options={opcoesCidades}
                      value={values.cidades}
                      onChange={(s) => setFieldValue('cidades', s || [])}
                      placeholder="Selecione cidade(s)..."
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingCid}
                      noOptionsMessage={() =>
                        gesCsvAtual ? 'Nenhuma cidade encontrada' : 'Selecione uma GE primeiro'
                      }
                    />
                  </Col>
                </Row>

                {/* Status */}
                <Row className="mb-2">
                  <Col>
                    <BsForm.Label>Status</BsForm.Label>
                    <div className="d-flex flex-wrap">
                      {[
                        { field: 'statusDisponivel', label: 'Disponível' },
                        { field: 'statusPreVenda', label: 'Pré Venda' },
                        { field: 'statusCadRapido', label: 'Cad Rápido' },
                        { field: 'statusBloqueado', label: 'Bloqueado' },
                        { field: 'statusAlugado', label: 'Alugado' },
                        { field: 'statusReservado', label: 'Reservado' },
                      ].map(({ field, label }) => (
                        <BsForm.Check
                          key={field}
                          inline
                          label={label}
                          type="checkbox"
                          id={`chk-${field}`}
                          checked={values[field]}
                          onChange={(e) => setFieldValue(field, e.target.checked)}
                          className="mr-3"
                        />
                      ))}
                      <BsForm.Check
                        inline
                        label="Carteira"
                        type="checkbox"
                        id="chk-carteira"
                        checked={values.carteira}
                        onChange={handleCarteiraChange}
                        className="mr-3"
                      />
                    </div>
                  </Col>
                </Row>

                {/* Estágio */}
                <Row className="mb-2">
                  <Col>
                    <BsForm.Label>Estágio</BsForm.Label>
                    <div className="d-flex flex-wrap">
                      {[
                        { field: 'estagioEstudos', label: 'Estudos' },
                        { field: 'estagioAprovacao', label: 'Aprovação' },
                        { field: 'estagioObras', label: 'Obras' },
                        { field: 'estagioRegistro', label: 'Registro' },
                        { field: 'estagioLancamento', label: 'Lançamento' },
                        { field: 'estagioFinalizado', label: 'Finalizado' },
                      ].map(({ field, label }) => (
                        <BsForm.Check
                          key={field}
                          inline
                          label={label}
                          type="checkbox"
                          id={`chk-${field}`}
                          checked={values[field]}
                          onChange={(e) => setFieldValue(field, e.target.checked)}
                          className="mr-3"
                        />
                      ))}
                    </div>
                  </Col>
                </Row>

                {/* Segmento */}
                <Row className="mb-2">
                  <Col>
                    <BsForm.Label>Segmento</BsForm.Label>
                    <div className="d-flex flex-wrap">
                      {[
                        { field: 'segmentoEstoque', label: 'Estoque' },
                        { field: 'segmentoAreaBruta', label: 'Área Bruta' },
                        { field: 'segmentoTerreno', label: 'Terreno' },
                        { field: 'segmentoProperty', label: 'Property' },
                      ].map(({ field, label }) => (
                        <BsForm.Check
                          key={field}
                          inline
                          label={label}
                          type="checkbox"
                          id={`chk-${field}`}
                          checked={values[field]}
                          onChange={(e) => setFieldValue(field, e.target.checked)}
                          className="mr-3"
                        />
                      ))}
                    </div>
                  </Col>
                </Row>

                {/* Opções extras */}
                <Row className="mb-2">
                  <Col md={4}>
                    <BsForm.Label>Ordenar por</BsForm.Label>
                    <div>
                      <BsForm.Check
                        inline
                        label="GE"
                        type="radio"
                        id="ord-ge"
                        checked={values.agrupamento === 0}
                        onChange={() => setFieldValue('agrupamento', 0)}
                      />
                      <BsForm.Check
                        inline
                        label="Cidade"
                        type="radio"
                        id="ord-cidade"
                        checked={values.agrupamento === 1}
                        onChange={() => setFieldValue('agrupamento', 1)}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <BsForm.Label>Lançados até</BsForm.Label>
                    <BsForm.Control
                      type="date"
                      value={values.dataLancamento}
                      onChange={(e) => setFieldValue('dataLancamento', e.target.value)}
                      size="sm"
                    />
                  </Col>
                  <Col md={4}>
                    <div className="d-flex flex-column">
                      <BsForm.Check
                        label="Levanta tempo de estoque"
                        type="checkbox"
                        id="chk-chDesde"
                        checked={values.levantaTempoEstoque}
                        onChange={(e) => setFieldValue('levantaTempoEstoque', e.target.checked)}
                        className="mb-1"
                      />
                      <BsForm.Check
                        label="Somente Saldo"
                        type="checkbox"
                        id="chk-somenteSaldo"
                        checked={values.somenteSaldo}
                        onChange={handleSomenteSaldoChange}
                        className="mb-1"
                      />
                      <BsForm.Check
                        label="Pontuação"
                        type="checkbox"
                        id="chk-pontuacao"
                        checked={values.pontuacao}
                        onChange={handlePontuacaoChange}
                        className="mb-1"
                      />
                    </div>
                  </Col>
                </Row>

                {/* Opções de pontuação + Reporting */}
                <Row className="mb-2">
                  <Col md={4}>
                    <BsForm.Label>Ordem Pontuação</BsForm.Label>
                    <Select
                      options={OPCOES_ORDEM_PONTUACAO}
                      value={OPCOES_ORDEM_PONTUACAO.find((o) => o.value === values.ordemPontuacao) || null}
                      onChange={(o) => setFieldValue('ordemPontuacao', o ? o.value : 0)}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={!values.pontuacao}
                      placeholder="Selecionar Ordem"
                    />
                  </Col>
                  <Col md={4}>
                    <div className="d-flex flex-column mt-4">
                      <BsForm.Check
                        label="Imprimir Custos (Reporting)"
                        type="checkbox"
                        id="chk-imprimirCustos"
                        checked={values.imprimirCustos}
                        onChange={(e) => setFieldValue('imprimirCustos', e.target.checked)}
                        className="mb-1"
                      />
                      <BsForm.Check
                        label="Totalizar (Reporting)"
                        type="checkbox"
                        id="chk-totaliza"
                        checked={values.totaliza}
                        onChange={(e) => setFieldValue('totaliza', e.target.checked)}
                        className="mb-1"
                      />
                    </div>
                  </Col>
                </Row>

                {/* Botões */}
                <Row>
                  <Col>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading || isSubmitting}
                      className="mr-2"
                    >
                      {isLoading ? 'Buscando...' : 'Buscar'}
                    </Button>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={handleLimpar}
                      disabled={isLoading}
                    >
                      Limpar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FiltrosImoveisEstoque;
